'use client';

/**
 * "What do we ask this shopper before their province pick lands?" — the client
 * half of the region scope prompt.
 *
 * The server owns the ANSWER (`/api/region-features`): whether to prompt at
 * all, and which of the two prompts — Quebec's `wink`, or the `standard`
 * "nationwide has more to choose from" confirmation that every other province
 * and state gets. This module owns the QUESTION: which place to ask about,
 * whether it is worth asking at all, and remembering that we already asked.
 *
 * THE TWO SKIPS ARE THE WHOLE DESIGN. A prompt that reappears is not a delight,
 * it is a nag, so before anything touches the network:
 *
 *  1. If the shopper has already STATED an ordering for this country, there is
 *     nothing to ask. Note "stated", not "has a location": `regionPriority` is
 *     written only by an actual answer, which is exactly why
 *     `buildSelectionPayload` refuses to default it. Absent ≠ country-first.
 *  2. If we have already shown the prompt for this country, we never show it
 *     again — including when it was dismissed. Dismissal is an answer about the
 *     PROMPT ("not now"), stored here; it is deliberately NOT an answer about
 *     the ordering, which stays unstated in the location so a future policy can
 *     re-ask (or so the shopper can state it from the region menu instead).
 *
 * ONE ANSWER PER COUNTRY COVERS BOTH VARIANTS, and that is a real trade: a
 * shopper who picks Ontario first, answers, and later picks Quebec never sees
 * the wink. Keeping a separate wink budget would mean asking the same question
 * twice, which is precisely the nag the two skips exist to prevent, and the
 * shopper the wink is FOR (detected in Quebec, picking Quebec) reaches it on
 * their first selection anyway. Both skips still short-circuit before the
 * network, so this stays zero requests per click for anyone already answered.
 *
 * Everything is a no-op on the server and degrades to "not eligible" on any
 * failure: blocked storage, an offline fetch, a malformed response.
 */

import { useEffect, useRef, useState } from 'react';
import type { RegionFeaturesResponse } from '@/app/api/region-features/route';
import type { RegionSelection } from '@/lib/region-selection';
import { getUserLocation } from '@/lib/user-location';
import { decodeLocationParam } from '@/lib/location-handoff';
import { isRegionPriority, WHOLE_COUNTRY_CODE } from '@/lib/subregion-pref';
import type { SubregionPromptVariant } from '@/lib/subregion-prompts';

/** Everything the prompt needs to render, once the server has said yes. */
export interface SubregionFeature {
  /** Canonical admin-1 code — what gets committed as the selection. */
  code: string;
  /** Registry name, in English. */
  name: string;
  /** Localized name for the page's language — what the shopper reads. */
  localName: string;
  /** `/flags/*.svg`, or null for a place that renders without one. */
  flagSrc: string | null;
  /**
   * WHICH prompt this place gets — Quebec's wink, or the standard "nationwide
   * has more" confirmation every other province and state gets.
   *
   * Server-decided, never re-derived here: answering "is this Quebec?" in the
   * browser would mean shipping the 48KB admin-1 registry to do it, which is
   * the whole reason `/api/region-features` exists.
   */
  variant: SubregionPromptVariant;
}

/** What the shopper did with the prompt. `dismissed` states no ordering. */
export type SubregionPromptChoice = 'subregion-first' | 'country-first' | 'dismissed';

export interface SubregionPromptAnswer {
  choice: SubregionPromptChoice;
  /** When it happened, so a future policy could expire a dismissal. */
  at: number;
}

/** What `useSubregionEligibility` reports. `subregion` is null unless eligible. */
export interface SubregionEligibility {
  eligible: boolean;
  subregion: SubregionFeature | null;
}

/**
 * One key per country: the shopper's answer is about Canada, not about the
 * planet, and someone who moves gets asked again in their new country.
 */
const PREFERENCE_KEY_PREFIX = 'subregion-preference-';

export function subregionPreferenceKey(countryCode: string): string {
  return `${PREFERENCE_KEY_PREFIX}${countryCode.trim().toUpperCase()}`;
}

function isChoice(value: unknown): value is SubregionPromptChoice {
  return value === 'subregion-first' || value === 'country-first' || value === 'dismissed';
}

/**
 * The stored answer, or null when we have never asked.
 *
 * TOLERANT ON PURPOSE: any non-empty value under the key counts as "asked",
 * even one this build cannot parse. Getting the shape wrong must never turn
 * into showing the prompt again — being unable to read the record is not
 * evidence that it does not exist.
 */
export function readSubregionPromptAnswer(countryCode: string): SubregionPromptAnswer | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(subregionPreferenceKey(countryCode));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;
      if (isChoice(record.choice)) {
        return { choice: record.choice, at: typeof record.at === 'number' ? record.at : 0 };
      }
    }
    return { choice: 'dismissed', at: 0 };
  } catch {
    // Unparseable but present, or storage blocked mid-read. Either way we have
    // no business asking again.
    return { choice: 'dismissed', at: 0 };
  }
}

/** Record the answer so the prompt never returns for this country. */
export function markSubregionPromptAnswered(
  countryCode: string,
  choice: SubregionPromptChoice,
): void {
  if (typeof window === 'undefined') return;
  try {
    const answer: SubregionPromptAnswer = { choice, at: Date.now() };
    localStorage.setItem(subregionPreferenceKey(countryCode), JSON.stringify(answer));
  } catch {
    // A full or blocked storage must never abort the selection behind it. The
    // cost is that the prompt may return — which is the safe direction to fail.
  }
}

/**
 * Has the shopper stated an ordering for this country?
 *
 * Reads the stored location, NOT the `iai_subregion` cookie: the cookie carries
 * the EFFECTIVE ordering (the server needs an answer, not a maybe), so it says
 * `country-first` for everyone who never chose. Only the location distinguishes
 * "wants nationwide" from "was never asked".
 */
export function hasStatedRegionPriority(countryCode: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return isRegionPriority(getUserLocation(countryCode)?.regionPriority);
  } catch {
    return false;
  }
}

// ─── The feature lookup ──────────────────────────────────────────────────────

/**
 * Promise cache, keyed by exactly what the answer depends on.
 *
 * Two callers ask the same question: this hook pre-warms on mount, and the
 * /region page re-checks at the moment of a selection. Caching the PROMISE (not
 * the value) means the second caller either shares the first's in-flight
 * request or resolves in a microtask — which is what lets a click handler await
 * this without the dialog feeling late.
 *
 * Misses are cached too. A config row does not change inside a page view, and
 * re-asking on every click would be a request per click.
 */
const featureCache = new Map<string, Promise<SubregionFeature | null>>();

/** Test/ops escape hatch — the /region page never needs this. */
export function clearSubregionFeatureCache(): void {
  featureCache.clear();
}

function parseFeature(payload: unknown): SubregionFeature | null {
  if (!payload || typeof payload !== 'object') return null;
  const data = payload as Partial<RegionFeaturesResponse>;
  if (data.eligible !== true) return null;
  if (typeof data.code !== 'string' || !data.code) return null;
  const name = typeof data.name === 'string' && data.name ? data.name : data.code;
  return {
    code: data.code,
    name,
    localName: typeof data.localName === 'string' && data.localName ? data.localName : name,
    flagSrc: typeof data.flagSrc === 'string' && data.flagSrc ? data.flagSrc : null,
    // 'standard' is the fallback for anything unrecognized, deliberately: the
    // wink has to be granted explicitly, so an older or garbled payload
    // downgrades to the neutral confirmation instead of putting Quebec's
    // greeting on a shopper in Texas.
    variant: data.variant === 'wink' ? 'wink' : 'standard',
  };
}

/**
 * Ask the server about one (country, subregion) pair. Null means "no easter egg
 * here" — including every failure, because a cosmetic feature has no business
 * distinguishing "off" from "unreachable".
 */
export function fetchSubregionFeature(
  countryCode: string,
  subregion: string,
  lang?: string,
): Promise<SubregionFeature | null> {
  const country = countryCode.trim().toUpperCase();
  const place = subregion.trim();
  if (!country || !place) return Promise.resolve(null);

  const key = `${country}:${place.toLowerCase()}:${lang ?? ''}`;
  const cached = featureCache.get(key);
  if (cached) return cached;

  const params = new URLSearchParams({ country, subregion: place });
  if (lang) params.set('lang', lang);

  const promise = fetch(`/api/region-features?${params.toString()}`)
    .then((response) => (response.ok ? response.json() : null))
    .then(parseFeature)
    .catch(() => null);

  featureCache.set(key, promise);
  return promise;
}

/**
 * The full gate, for the moment a selection is made: both skips, then the
 * server. Returns the feature to prompt with, or null to commit straight
 * through.
 *
 * The /region page calls this instead of trusting the hook's pre-warmed answer,
 * because the place a shopper CLICKS is not necessarily the place they are
 * detected in — someone in Ottawa picking Québec deserves the same wink.
 */
export async function resolveSubregionPromptTarget(
  countryCode: string,
  subregion: string,
  lang?: string,
): Promise<SubregionFeature | null> {
  if (typeof window === 'undefined') return null;
  if (!countryCode || !subregion) return null;
  if (readSubregionPromptAnswer(countryCode)) return null;
  if (hasStatedRegionPriority(countryCode)) return null;
  return fetchSubregionFeature(countryCode, subregion, lang);
}

// ─── What each answer means ──────────────────────────────────────────────────

/**
 * PURE. The selection to commit, given what the shopper originally clicked and
 * what they answered. Separated from the dialog so the three outcomes are
 * unit-testable against `buildSelectionPayload` without rendering or
 * navigating — the payload is the part that must not drift.
 *
 * Registry-free: `WHOLE_COUNTRY_CODE` is the deliberate mirror of
 * `ALL_REGIONS_SENTINEL` in `lib/subregion-pref`, so the easter egg does not
 * drag 48KB of Natural Earth data into the client bundle to name one constant.
 * `subregion-pref.test.ts` already pins the two to the same string.
 *
 * The three outcomes:
 *  - `subregion-first`: the shopper wants this place first. Commit THE PLACE,
 *    not whatever they happened to click, so answering from a city pin lands on
 *    the same province a map click would have.
 *  - `country-first`: nationwide scope, but the city SURVIVES. "Show me the
 *    whole country" is a statement about product scope, not a request to forget
 *    where they live — the header badge and delivery estimates stay local.
 *  - `dismissed`: exactly what they clicked, with NO `regionPriority`. Stamping
 *    a default here would record an ordering the shopper never stated and erase
 *    the difference the whole feature is built on.
 */
export function applySubregionPromptChoice(
  base: RegionSelection,
  choice: SubregionPromptChoice,
  feature: SubregionFeature,
): RegionSelection {
  // Removed rather than overwritten: `buildSelectionPayload` only writes the
  // field when it is a valid priority, and an explicit `undefined` would still
  // be a key someone could later read as meaningful.
  const { regionPriority: _stated, ...withoutPriority } = base;

  if (choice === 'subregion-first') {
    return {
      ...withoutPriority,
      subregion: feature.code,
      regionPriority: 'subregion-first',
      regionSource: 'explicit',
    };
  }

  if (choice === 'country-first') {
    return {
      ...withoutPriority,
      subregion: WHOLE_COUNTRY_CODE,
      regionPriority: 'country-first',
      regionSource: 'explicit',
    };
  }

  return withoutPriority;
}

// ─── Candidate resolution ────────────────────────────────────────────────────

/** The `loc` handoff param, if it is still on the URL. */
function subregionFromLocParam(countryCode: string): string | null {
  try {
    // Read `window.location` rather than `useSearchParams()`: this is a
    // backstop, and taking the router hook would force every consumer of this
    // module into a Suspense boundary for a param that `LocationHandoff` has
    // usually already consumed and stripped.
    const raw = new URLSearchParams(window.location.search).get('loc');
    if (!raw) return null;
    const location = decodeLocationParam(raw);
    if (!location || location.country !== countryCode.trim().toUpperCase()) return null;
    return location.region?.trim() || null;
  } catch {
    return null;
  }
}

/** IP geolocation. Emits a CODE on Vercel and a NAME via ipinfo — both fine. */
async function subregionFromDetection(countryCode: string): Promise<string | null> {
  try {
    const response = await fetch('/api/detect-region');
    if (!response.ok) return null;
    const data: unknown = await response.json();
    if (!data || typeof data !== 'object') return null;
    const detected = data as { country?: unknown; region?: unknown };
    if (typeof detected.country !== 'string') return null;
    // A visitor detected in another country tells us nothing about this one.
    if (detected.country.trim().toUpperCase() !== countryCode.trim().toUpperCase()) return null;
    return typeof detected.region === 'string' && detected.region.trim()
      ? detected.region.trim()
      : null;
  } catch {
    return null;
  }
}

/**
 * Where the shopper is, in the order the sources deserve to be believed:
 * what they previously chose, then a handoff from another origin, then an IP
 * guess. Null when nothing knows.
 */
async function resolveCandidateSubregion(countryCode: string): Promise<string | null> {
  const stored = getUserLocation(countryCode)?.region?.trim();
  if (stored) return stored;

  const fromParam = subregionFromLocParam(countryCode);
  if (fromParam) return fromParam;

  return subregionFromDetection(countryCode);
}

// ─── The hook ────────────────────────────────────────────────────────────────

const NOT_ELIGIBLE: SubregionEligibility = { eligible: false, subregion: null };

/**
 * Which prompt THIS shopper's own place would get, resolved once on mount.
 *
 * Its job is to be early: by pre-warming the feature cache for the place the
 * visitor appears to be in, the check that runs on their actual click resolves
 * from cache instead of from the network, so the prompt lands with the click
 * rather than a moment after it.
 *
 * Skips entirely — no detection, no fetch, nothing — when the shopper has
 * already stated an ordering for this country or has already been asked.
 */
export function useSubregionEligibility(countryCode: string, lang?: string): SubregionEligibility {
  const [eligibility, setEligibility] = useState<SubregionEligibility>(NOT_ELIGIBLE);
  // Rendered value is state; the guard has to be a ref so a re-render mid-flight
  // cannot resurrect a resolution we already decided to abandon.
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    setEligibility(NOT_ELIGIBLE);

    const country = countryCode?.trim().toUpperCase();
    if (!country) return;
    // The skips, before any I/O — this is the point of the whole module.
    if (readSubregionPromptAnswer(country)) return;
    if (hasStatedRegionPriority(country)) return;

    void (async () => {
      const candidate = await resolveCandidateSubregion(country);
      if (!activeRef.current || !candidate) return;

      const feature = await fetchSubregionFeature(country, candidate, lang);
      if (!activeRef.current || !feature) return;

      setEligibility({ eligible: true, subregion: feature });
    })();

    return () => {
      activeRef.current = false;
    };
  }, [countryCode, lang]);

  return eligibility;
}
